import { Box, Button, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { FilterKeys } from '@schooly/api';
import { PropertyTypeSelectMultiple, SubjectSelectMultiple } from '@schooly/components/filters';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { CrossIcon, ModalContent, ModalFooter, ModalHeader } from '@schooly/style';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../hooks/useSchool';

export type AddStaffFilters = {
  [FilterKeys.Status]?: string[];
  [FilterKeys.House]?: string[];
  [FilterKeys.Subject]?: string[];
};

export type AddStaffFiltersKey = FilterKeys.Status | FilterKeys.House | FilterKeys.Subject;

type AddStaffFiltersPopupProps = {
  filtersAvailable: AddStaffFiltersKey[];
  filters: AddStaffFilters;
  defaultFilters: AddStaffFilters;
  onClose: () => void;
  onSetFilters: (v: AddStaffFilters) => void;
};

export const AddStaffFiltersPopup: FC<AddStaffFiltersPopupProps> = ({
  filtersAvailable,
  onClose,
  onSetFilters,
  filters: initialFilters,
  defaultFilters,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId } = useSchool();

  const [filters, setFilters] = useState<AddStaffFilters>(initialFilters);

  const handleApply = useCallback(() => {
    onSetFilters(filters);

    onClose();
  }, [onSetFilters, filters, onClose]);

  const handleClear = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  const {
    onSelectStaffStatusId,
    onSelectSubjectId,
    onSelectHouseId,
    onClearStatus,
    onClearSubject,
    onClearHouse,
  } = useMemo(() => {
    const updateFilter = (key: AddStaffFiltersKey) => (id: string) => {
      setFilters((v) => ({
        ...v,
        [key]:
          v[key] && v[key]?.includes(id)
            ? v[key]?.filter((ct) => ct !== id)
            : [...(v[key] || []), id],
      }));
    };
    const clearFilter = (key: AddStaffFiltersKey) => () => {
      setFilters((v) => ({
        ...v,
        [key]: [],
      }));
    };

    return {
      onSelectStaffStatusId: updateFilter(FilterKeys.Status),
      onSelectSubjectId: updateFilter(FilterKeys.Subject),
      onSelectHouseId: updateFilter(FilterKeys.House),
      onClearStatus: clearFilter(FilterKeys.Status),
      onClearHouse: clearFilter(FilterKeys.House),
      onClearSubject: clearFilter(FilterKeys.Subject),
    };
  }, []);

  if (!schoolId) return null;
  return (
    <Dialog
      fullWidth
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        zIndex: theme.zIndex.modal + 1,

        '& .MuiDialog-paperFullWidth': {
          width: 600,
          height: 'unset',
        },
      })}
      open
      onClose={onClose}
    >
      <ModalHeader
        active
        title={formatMessage(
          { id: 'groups-FilterUserType' },
          { userType: formatMessage({ id: 'userType-staff-plural' }) },
        )}
      >
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent active>
        {filtersAvailable.includes(FilterKeys.Status) && (
          <FilterRow label={formatMessage({ id: 'schoolProperty-Status' })}>
            <PropertyTypeSelectMultiple
              borderOnHover
              allowArchived
              schoolId={schoolId}
              onClear={onClearStatus}
              optionsTitle={formatMessage({ id: 'schoolProperty-Status-plural' })}
              userRole={SchoolUserRole.Staff}
              propertyType={SchoolPropertyType.Status}
              selectedIds={filters.status || []}
              onSelectId={onSelectStaffStatusId}
            />
          </FilterRow>
        )}

        {filtersAvailable.includes(FilterKeys.House) && (
          <FilterRow label={formatMessage({ id: 'schoolProperty-House' })}>
            <PropertyTypeSelectMultiple
              borderOnHover
              allowArchived
              schoolId={schoolId}
              onClear={onClearHouse}
              optionsTitle={formatMessage({ id: 'schoolProperty-House-plural' })}
              userRole={SchoolUserRole.Student}
              propertyType={SchoolPropertyType.House}
              selectedIds={filters.house || []}
              onSelectId={onSelectHouseId}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.Subject) && (
          <FilterRow label={formatMessage({ id: 'groups-GroupSubject' })}>
            <SubjectSelectMultiple
              borderOnHover
              schoolId={schoolId}
              onClear={onClearSubject}
              optionsTitle={formatMessage({ id: 'groups-GroupSubject-plural' })}
              selectedIds={filters.subject || []}
              onSelectSubjectId={onSelectSubjectId}
            />
          </FilterRow>
        )}
      </ModalContent>
      <ModalFooter active>
        <Button variant="outlined" onClick={handleClear}>
          {formatMessage({ id: 'filter-Clear' })}
        </Button>
        <Button onClick={handleApply}>{formatMessage({ id: 'filter-Apply' })}</Button>
      </ModalFooter>
    </Dialog>
  );
};

const FilterRow: FC<PropsWithChildren<{ label: string }>> = ({ label, children }) => (
  <Stack flexDirection="row" alignItems="center" gap={2}>
    <Box sx={{ flex: '1 1 15%' }}>
      <Typography color="common.grey2">{label}</Typography>
    </Box>
    <Box sx={{ flex: '1 1 85%' }}>{children}</Box>
  </Stack>
);
